/**
 * useRuleAuditsDrilldown Hook
 *
 * Fetches paginated list of audits for a specific rule
 * Supports both provider and admin endpoints via organizationId parameter
 */

import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import type { RuleAuditsListResponse } from '@store/generated/jotaudit';

import { useGetAdminRuleAuditsDrilldownQuery } from '../store/adminApi';
import { useGetRuleAuditsDrilldownQuery } from '../store/api';

interface UseRuleAuditsDrilldownParams {
  /** Rule ID */
  ruleId: string;
  /** Organization ID for admin mode (if provided, uses admin endpoint) */
  organizationId?: string;
  /** Compliance status filter: "yes", "warning", or "no" */
  complianceStatus?: 'yes' | 'warning' | 'no';
  /** Period in days */
  days?: number;
  /** Start date (ISO format) */
  dateFrom?: string;
  /** End date (ISO format) */
  dateTo?: string;
  /** Comma-separated provider IDs */
  userIds?: string;
  /** Page number (default 1) */
  page?: number;
  /** Items per page (default 20, max 100) */
  perPage?: number;
  /** Skip query if ruleId is not provided */
  skip?: boolean;
}

interface UseRuleAuditsDrilldownResult {
  /** Audit list data */
  data: RuleAuditsListResponse | undefined;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
  /** List of audits */
  audits: RuleAuditsListResponse['audits'] | null;
  /** Total count */
  total: number;
  /** Current page */
  currentPage: number;
  /** Items per page */
  itemsPerPage: number;
  /** Whether there are more pages */
  hasMore: boolean;
  /** Rule name */
  ruleName: string | null;
}

export const useRuleAuditsDrilldown = (
  params: UseRuleAuditsDrilldownParams,
): UseRuleAuditsDrilldownResult => {
  const {
    ruleId,
    organizationId,
    complianceStatus,
    days,
    dateFrom,
    dateTo,
    userIds,
    page = 1,
    perPage = 20,
    skip,
  } = params;

  const filterParams = {
    ...(complianceStatus && { complianceStatus }),
    ...(days !== undefined && { days }),
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo }),
    ...(userIds && { userIds }),
    ...(page !== undefined && { page }),
    ...(perPage !== undefined && { perPage }),
  };

  // Use admin endpoint if organizationId provided, otherwise use provider endpoint
  const adminQuery = useGetAdminRuleAuditsDrilldownQuery(
    organizationId && !skip && ruleId
      ? {
          ruleId,
          organizationId,
          ...filterParams,
        }
      : skipToken,
  );

  const providerQuery = useGetRuleAuditsDrilldownQuery(
    !organizationId && !skip && ruleId
      ? {
          ruleId,
          ...filterParams,
        }
      : skipToken,
  );

  // Use whichever query is active
  const { data, isLoading, error, refetch } = organizationId ? adminQuery : providerQuery;

  const result = useMemo(() => {
    if (!data) {
      return {
        audits: null,
        total: 0,
        currentPage: 1,
        itemsPerPage: perPage,
        hasMore: false,
        ruleName: null,
      };
    }

    return {
      audits: data.audits || null,
      total: data.total || 0,
      currentPage: data.page || 1,
      itemsPerPage: data.per_page || perPage,
      hasMore: data.has_more || false,
      ruleName: data.rule_name || null,
    };
  }, [data, perPage]);

  return {
    data,
    isLoading,
    error: error ? 'Failed to load rule audits' : null,
    refetch,
    ...result,
  };
};

export default useRuleAuditsDrilldown;

